import type { DomainHelpDnsRecord } from '@/api/generated';
import type {
  PutWhitelabelHost,
  WhitelabelDomainDto,
  WhitelabelTemplateDto,
} from '@/api/saasAuthTypes';

export type DomainStepId = 'domain' | 'records' | 'live';

export const DNS_PROVIDERS = [
  'GODADDY',
  'NAMECHEAP',
  'CLOUDFLARE',
  'ROUTE53',
  'AZURE_DNS',
  'GOOGLE',
  'WIX',
  'SQUARESPACE',
  'BLUEHOST',
  'HOSTINGER',
  'IONOS',
  'HOVER',
  'DNSIMPLE',
  'DIGITALOCEAN',
  'NAME_COM',
  'DREAMHOST',
  'HOSTGATOR',
  'SITEGROUND',
  'OVH',
  'PORKBUN',
] as const;

export type DnsProviderKey = (typeof DNS_PROVIDERS)[number];

export const OTHER_PROVIDER = 'OTHER';

export const ROOT_DOMAIN_PATTERN = /^[a-z0-9-]+(\.[a-z0-9-]+)+$/;
export const APP_PREFIX_PATTERN = /^[a-z0-9-]+$/;

export function stepFor(dto: WhitelabelDomainDto | null | undefined): DomainStepId {
  if (!dto || dto.configured !== true) return 'domain';
  if (dto.state === 'ACTIVE') return 'live';
  return 'records';
}

/** The tenant's BRAND host, when its domain has one. The reseller bootstrap carries no brand
 *  origin to fall back to (TMMRSL-0028). */
export function brandHref(dto: WhitelabelDomainDto | null | undefined): string | null {
  const brand = dto?.hostnames.find((row) => row.hostnameType === 'BRAND' && row.hostname);
  return brand?.hostname ? `https://${brand.hostname}` : null;
}

export function appHostOf(dto: WhitelabelDomainDto | null | undefined): string {
  const app = dto?.hostnames.find((row) => row.hostnameType === 'APP' && row.hostname);
  if (app?.hostname) return app.hostname;
  if (dto?.domain) return `app.${dto.domain}`;
  return '';
}

export function recordsForHelper(dto: WhitelabelDomainDto | null | undefined): DomainHelpDnsRecord[] {
  const rows = dto?.dnsRecords ?? [];
  return rows.slice(0, 12).map((row) => ({
    host: row.host,
    type: row.type || 'CNAME',
    target: row.target,
  }));
}

export function templateToHosts(
  template: WhitelabelTemplateDto,
  appPrefix: string,
): PutWhitelabelHost[] {
  const prefix = appPrefix.trim().toLowerCase() || 'app';
  let usedPrimaryApp = false;
  return (template.hosts ?? []).map((host) => {
    const isPrimaryApp = host.hostnameType === 'APP' && !usedPrimaryApp;
    if (isPrimaryApp) usedPrimaryApp = true;
    const entry: PutWhitelabelHost = {
      prefix: isPrimaryApp ? prefix : host.prefix,
      hostnameType: host.hostnameType,
    };
    if (host.appId !== undefined) entry.appId = host.appId;
    return entry;
  });
}

export function matchProviderKey(value: string | undefined): DnsProviderKey | typeof OTHER_PROVIDER {
  if (!value) return OTHER_PROVIDER;
  const upper = value.trim().toUpperCase().replace(/[\s-]+/g, '_');
  const found = DNS_PROVIDERS.find((key) => key === upper);
  return found ?? OTHER_PROVIDER;
}

export function normalizeRootDomain(raw: string): string {
  return raw.trim().toLowerCase();
}

export function isValidRootDomain(value: string): boolean {
  const normalized = normalizeRootDomain(value);
  if (normalized.includes('://')) return false;
  return ROOT_DOMAIN_PATTERN.test(normalized);
}

export function isValidAppPrefix(value: string): boolean {
  return APP_PREFIX_PATTERN.test(value.trim().toLowerCase());
}
